import * as React from "react";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
  type SortingState
} from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";

import { cn } from "@/lib/utils";

import { Skeleton } from "./skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "./table";

type DataTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  /**
   * `bleed`: list pages, edge to edge with the outer cells on the 24px page
   * gutter, no header fill, `line-2` under the header. Don't inset it.
   * `card`: settings and dashboards, a ringed card with a 36px chrome band.
   */
  variant?: "bleed" | "card";
  /** 40px single-line rows or 48px two-line rows. */
  rowHeight?: "single" | "double";
  getRowId?: (row: T, index: number) => string;
  getRowTestId?: (row: T) => string;
  onRowClick?: (row: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  /** Row the keyboard or peek is focused on. */
  activeRowId?: string | null;
  isLoading?: boolean;
  /** Rendered in place of the body when there are no rows. */
  empty?: React.ReactNode;
  /** Enables header-click sorting (client side unless `onSortingChange`). */
  sortable?: boolean;
  sorting?: SortingState;
  onSortingChange?: (next: SortingState) => void;
  /** Sticks within the table's own scroll box; give the table a height. */
  stickyHeader?: boolean;
  className?: string;
};

const SKELETON_ROWS = 6;

/** The one table: header 12px `fg-2`, rows divided by `line-1`, hover `fill-1`. */
function DataTable<T>({
  data,
  columns,
  variant = "bleed",
  rowHeight = "single",
  getRowId,
  getRowTestId,
  onRowClick,
  activeRowId,
  isLoading = false,
  empty,
  sortable = false,
  sorting: controlledSorting,
  onSortingChange,
  stickyHeader = true,
  className
}: DataTableProps<T>) {
  const [localSorting, setLocalSorting] = React.useState<SortingState>([]);
  const sorting = controlledSorting ?? localSorting;
  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(sorting) : updater;
      if (onSortingChange) onSortingChange(next);
      else setLocalSorting(next);
    },
    enableSorting: sortable,
    manualSorting: Boolean(onSortingChange),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });

  const card = variant === "card";
  const { rows } = table.getRowModel();
  const colCount = table.getVisibleLeafColumns().length;
  const rowClass = rowHeight === "double" ? "h-12" : "h-10";

  const renderRow = (row: Row<T>) => (
    <TableRow
      key={row.id}
      data-state={row.id === activeRowId ? "selected" : undefined}
      data-testid={getRowTestId?.(row.original)}
      onClick={onRowClick ? (e) => onRowClick(row.original, e) : undefined}
      className={cn(rowClass, onRowClick && "cursor-pointer")}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          key={cell.id}
          className={cn(
            "h-auto",
            cell.column.columnDef.meta?.isNumeric && "text-right tabular-nums",
            cell.column.columnDef.meta?.className
          )}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <div
      data-slot="data-table"
      data-variant={variant}
      className={cn(
        "min-w-0 overflow-auto",
        card
          ? "rounded-xl bg-elevated ring-1 ring-line-2"
          : "[&_tr>*:first-child]:pl-6 [&_tr>*:last-child]:pr-6",
        className
      )}
    >
      <Table containerClassName="overflow-visible">
        <TableHeader
          className={cn(
            stickyHeader && "sticky top-0 z-10",
            card ? "bg-chrome [&_tr]:border-line-1" : "bg-surface"
          )}
        >
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id}>
              {group.headers.map((header) => {
                const canSort = sortable && header.column.getCanSort();
                const dir = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    aria-sort={
                      dir === "asc"
                        ? "ascending"
                        : dir === "desc"
                        ? "descending"
                        : undefined
                    }
                    className={cn(
                      header.column.columnDef.meta?.isNumeric && "text-right",
                      header.column.columnDef.meta?.className
                    )}
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                        className="inline-flex items-center gap-1 rounded-xs outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {dir === "asc" ? (
                          <ArrowUpIcon className="size-3" />
                        ) : dir === "desc" ? (
                          <ArrowDownIcon className="size-3" />
                        ) : null}
                      </button>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }, (_, i) => (
              <TableRow key={`skeleton-${i}`} className={rowClass}>
                {Array.from({ length: colCount }, (_, j) => (
                  <TableCell key={j} className="h-auto">
                    <Skeleton
                      className={cn("h-3", j === 0 ? "w-40" : "w-20")}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length ? (
            rows.map(renderRow)
          ) : empty ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={colCount} className="h-auto p-0">
                {empty}
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
    </div>
  );
}

export { DataTable };
export type { DataTableProps };
